import { Injectable } from '@angular/core';
import { IntervalService } from '../interval/interval.service';
import { CallProblem, CallProblemSeverity } from 'src/app/models/call-problem/call-problem';
import { AudioProfileType } from 'src/app/models/remediation-profile/audio-profile';
import { StreamTier, TIER_CONFIGS } from 'src/app/models/stream-tier/stream-tier';
import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { remediationRules, RemediationTypes } from './issue-remediation.rules';
import { DataChannelService } from '../data-channel/data-channel.service';
import { BandwidthProblems } from '../issue-detection/rules/bandwidth';
import { ParticipantsListService } from '../participants-list/participants-list.service';
import { RemediationProfile } from 'src/app/models/remediation-profile/remediation-profile';
import { FeatureSwitchService } from '../feature-switch/feature-switch.service';
import { Remediation } from 'src/app/models/remediation-profile/remediation';
import { VirtualBackgroundService } from '../virtual-background/virtual-background.service';
import { NotificationService } from '../notification/notification.service';
import { RemediationProfileGenerator } from './remediation-profile-generator';
import { RemediationProfileApplier } from './remediation-profile-applier';
import { RemediationUpgradeDowngrade } from './remediation-upgrade-downgrade';

declare global {
  interface RTCRtpEncodingParameters {
    maxFrameRate: number;
  }
  interface Window {
    forceProfile: string;
    forceShareProfile: string;
  }
}

type RemediationNotification = {
  notification: boolean;
  message: string;
  color: string;
};

const remediationProblems = {
  [RemediationTypes.bitrate]: {
    notification: false,
    message: 'remediation.problems.bitrate',
    color: 'warning',
  },
  [RemediationTypes.resolution]: {
    notification: false,
    message: 'remediation.problems.resolution',
    color: 'warning',
  },
};

@Injectable({
  providedIn: 'root'
})
export class IssueRemediationService {

  participantRemediations: Remediation;
  shareRemediations: Remediation;
  screenShareProfile: RemediationProfile;
  savedProfiles: { [key: string]: RemediationProfile };
  downgradeTime: number;
  upgradeTime: number;
  roomId: string;
  userId: string;
  shareVideoTrack: MediaStreamTrack;
  shareAudioTrack: MediaStreamTrack;
  lastResolution: number;

  private profileGenerator: RemediationProfileGenerator;
  private profileApplier: RemediationProfileApplier;
  private upgradeDowngrade: RemediationUpgradeDowngrade;

  constructor(
    private interval: IntervalService,
    private logging: LoggingService,
    private dataChannel: DataChannelService,
    private participants: ParticipantsListService,
    private featureSwitch: FeatureSwitchService,
    private background: VirtualBackgroundService,
    private notification: NotificationService,
  ) {
    this.participantRemediations = new Remediation();
    this.shareRemediations = new Remediation();
    this.downgradeTime = 5000;
    this.upgradeTime = 15000;

    this.profileGenerator = new RemediationProfileGenerator(this.logging);
    this.profileApplier = new RemediationProfileApplier(this.logging, this.participants, this.background);
    this.upgradeDowngrade = new RemediationUpgradeDowngrade(this.logging, this.featureSwitch, this.background);
  }

  public start(roomId: string, userId: string) {
    this.roomId = roomId;
    this.userId = userId;

    this.interval.setInterval('remediation', () => {
      Object.keys(this.participantRemediations.local).forEach((participant) => {
        this.upgrade(participant);
      });
      Object.keys(this.shareRemediations.local).forEach((participant) => {
        this.upgrade(participant, true);
      });
      if (this.background.resolution !== this.lastResolution) {
        this.lastResolution = this.background.resolution;
        setTimeout(() => {
          Object.keys(this.participantRemediations.local).forEach((participant) => {
            this.applyRemediations(participant, false);
          });
        }, 300);
      }
    }, 1000);
  }

  public stop() {
    this.interval.clearInterval('remediation');
    this.roomId = null;
    this.userId = null;
  }

  public startParticipant(participant: string) {
    if (this.participantRemediations.local[participant]) {
      this.reconnectParticipant(participant);
      return;
    }
    this.upgradeTime += 2000;
    this.participantRemediations.last[participant] = Date.now();
    if (this.participantRemediations.profiles) {
      if (this.savedProfiles || this.shareVideoTrack) {
        if (this.background.isActive) {
          this.background.forceResolution = true;
          this.background.savedResolution = this.background.resolution;
          this.background.resolution = 0.25;
        }
        setTimeout(() => {
          this.savedProfiles[participant] = this.participantRemediations.profiles[0];
          this.setLocalProfile(participant, this.screenShareProfile, false);
          this.applyRemediations(participant, false);
        }, 300);
      } else {
        this.setLocalProfile(participant, this.participantRemediations.profiles[0]);
        this.applyRemediations(participant, false);
      }
    }
    if (this.shareVideoTrack) {
      this.shareRemediations.last[participant] = Date.now();
      if (this.shareRemediations.profiles) {
        this.setLocalProfile(participant, this.shareRemediations.profiles[0], true);
        this.applyRemediations(participant, true);
      }
    }
    this.dataChannel.registerCallback(participant, 'remediation', (data) => {
      if (data.bitrate) {
        this.checkProblem(new CallProblem(participant, BandwidthProblems.bandwidth_out, CallProblemSeverity.warning), data.sharing);
      }
      if (data.resolution) {
        const profile = this.findNewProfile(participant, data.sharing);
        let remediations: Remediation;
        if (data.sharing) {
          remediations = this.shareRemediations;
        } else {
          remediations = this.participantRemediations;
        }
        if (profile && profile.id !== remediations.local[participant].id) {
          remediations.last[participant] = Date.now();
          this.setLocalProfile(participant, profile, data.sharing);
          this.logRemediation(participant, 'Downgrade profile to remote', profile, data.sharing);
          this.logging.log('IssueRemediationService: downgrade profile to remote', LoggingChannels.Remediation, participant, profile.id, data.sharing);
          this.applyRemediations(participant, data.sharing);
        }
      }
    });
    this.dataChannel.registerCallback(participant, 'remediation-profile', (data) => {
      const profileParts = (data.profile as string).split('-');
      const profile = new RemediationProfile(
        parseInt(profileParts[0], 10),
        parseInt(profileParts[1], 10),
        parseInt(profileParts[2], 10),
        parseInt(profileParts[3], 10)
      );
      if (data.sharing) {
        this.shareRemediations.remote[participant] = profile;
      } else {
        this.participantRemediations.remote[participant] = profile;
      }
    });
  }

  public stopParticipant(participant: string) {
    this.upgradeTime -= 2000;
    delete this.participantRemediations.local[participant];
    delete this.participantRemediations.remote[participant];
    delete this.participantRemediations.last[participant];
    if (this.shareRemediations.local[participant]) {
      delete this.shareRemediations.local[participant];
    }
    if (this.shareRemediations.remote[participant]) {
      delete this.shareRemediations.remote[participant];
    }
    if (this.shareRemediations.last[participant]) {
      delete this.shareRemediations.last[participant];
    }
  }

  public reconnectParticipant(participant: string) {
    this.applyRemediations(participant, false);
    if (this.shareVideoTrack) {
      this.applyRemediations(participant, true);
    }
  }

  public startSharing() {
    Object.keys(this.participantRemediations.local).forEach((participant) => {
      this.shareRemediations.last[participant] = Date.now();
      this.setLocalProfile(participant, this.shareRemediations.profiles[0], true);
    });
    this.setSharingProfiles();
  }

  public stopSharing() {
    this.shareVideoTrack = null;
    this.shareAudioTrack = null;
    Object.keys(this.shareRemediations.local).forEach((participant) => {
      delete this.shareRemediations.local[participant];
    });
    Object.keys(this.shareRemediations.remote).forEach((participant) => {
      delete this.shareRemediations.remote[participant];
    });
    Object.keys(this.shareRemediations.last).forEach((participant) => {
      delete this.shareRemediations.last[participant];
    });
    this.restoreProfiles();
  }

  public onTierChanged(participantId: string, newTier: StreamTier): void {
    const tierConfig = TIER_CONFIGS[newTier];
    if (!tierConfig.sendVideo && this.participantRemediations.local[participantId]) {
      // Background tier: constrain to audio-only profile (no video)
      this.logging.log('IssueRemediationService: tier changed to background, constraining profile', LoggingChannels.Remediation, participantId);
      const currentProfile = this.participantRemediations.local[participantId];
      const audioOnlyProfile = new RemediationProfile(
        0, 0, 0,
        currentProfile.audio.bitrate as AudioProfileType,
      );
      this.setLocalProfile(participantId, audioOnlyProfile, false);
      this.applyRemediations(participantId, false);
    }
    if (newTier === StreamTier.Visible && this.participantRemediations.local[participantId]) {
      // Visible tier: constrain to max 360p/15fps
      const currentProfile = this.participantRemediations.local[participantId];
      if (currentProfile.video.width > tierConfig.maxWidth || currentProfile.video.height > tierConfig.maxHeight) {
        const constrainedProfile = new RemediationProfile(
          Math.min(currentProfile.video.width, tierConfig.maxWidth),
          Math.min(currentProfile.video.height, tierConfig.maxHeight),
          Math.min(currentProfile.video.framerate, tierConfig.maxFps),
          currentProfile.audio.bitrate as AudioProfileType,
        );
        this.setLocalProfile(participantId, constrainedProfile, false);
        this.applyRemediations(participantId, false);
      }
    }
  }

  public setSharingProfiles() {
    this.savedProfiles = {};
    if (this.background.isActive) {
      this.background.forceResolution = true;
      this.background.savedResolution = this.background.resolution;
      this.background.resolution = 0.25;
    }
    setTimeout(() => {
      Object.keys(this.participantRemediations.local).forEach((participant) => {
        this.savedProfiles[participant] = this.participantRemediations.local[participant];
        this.setLocalProfile(participant, this.screenShareProfile, false);
        this.applyRemediations(participant, false);
      });
    }, 300);
  }

  public restoreProfiles() {
    if (this.background.isActive) {
      this.background.forceResolution = false;
      this.background.resolution = this.background.savedResolution;
    }
    setTimeout(() => {
      if (this.savedProfiles) {
        Object.keys(this.savedProfiles).forEach((participant) => {
          this.setLocalProfile(participant, this.savedProfiles[participant], false);
          this.applyRemediations(participant, false);
        });
      }
      this.savedProfiles = null;
    }, 300);
  }

  public checkProblem(problem: CallProblem, sharing = false): boolean {
    if (remediationRules[problem.message]) {
      for (const remediation of remediationRules[problem.message]) {
        switch (remediation.type) {
          case RemediationTypes.participants:
            if (remediation.value < this.participants.visibleParticipantNb) {
              this.logging.log('IssueRemediationService: Decrease nb visible participants', LoggingChannels.Remediation);
              this.participants.visibleParticipantNb = remediation.value;
            }
            break;
          case RemediationTypes.bitrate:
            if (problem.participant) {
              this.logging.log('IssueRemediationService: Decrease Bitrate', LoggingChannels.Remediation, problem.participant);
              this.logRemediation(problem.participant, 'Decrease Bitrate', this.participantRemediations.local[problem.participant], sharing);
              this.downgrade(problem.participant, sharing);
              this.display(remediationProblems[RemediationTypes.bitrate]);
            } else {
              this.checkProblems(problem, sharing);
            }
            break;
          case RemediationTypes.resolution:
            if (problem.participant) {
              this.logging.log('IssueRemediationService: Decrease Resolution', LoggingChannels.Remediation, problem.participant);
              this.logRemediation(problem.participant, 'Decrease Resolution', this.participantRemediations.local[problem.participant], sharing);
              this.downgrade(problem.participant, sharing);
              this.display(remediationProblems[RemediationTypes.resolution]);
            } else {
              this.background.downgrade();
              setTimeout(() => {
                this.checkProblems(problem, sharing);
                this.logRemediation(problem.participant, 'Decrease Background Resolution', this.participantRemediations.local[problem.participant], sharing);
              }, 300);
            }
            break;
          case RemediationTypes.remote_bitrate:
            if (!this.shareVideoTrack || !sharing) {
              if (problem.participant) {
                this.logging.log('IssueRemediationService: Remote Bitrate', LoggingChannels.Remediation, problem.participant);
                this.logRemediation(problem.participant, 'Remote Bitrate', this.participantRemediations.local[problem.participant], sharing);
                this.dataChannel.send(problem.participant, 'remediation', {
                  bitrate: remediation.value,
                  sharing,
                });
              } else {
                this.checkProblems(problem, sharing);
              }
            }
            break;
          case RemediationTypes.remote_resolution:
            if (!this.shareVideoTrack || !sharing) {
              if (problem.participant) {
                if (!this.background.isActive || this.background.resolution <= 0.75) {
                  this.logging.log('IssueRemediationService: Remote Resolution', LoggingChannels.Remediation, problem.participant);
                  this.logRemediation(problem.participant, 'Remote Resolution', this.participantRemediations.local[problem.participant], sharing);
                  this.dataChannel.send(problem.participant, 'remediation', {
                    resolution: remediation.value,
                    sharing,
                  });
                }
              } else {
                this.checkProblems(problem, sharing);
              }
            }
            break;
        }
      }

      return true;
    }

    return false;
  }

  public setMaxProfile(width: number, height: number, framerate: number, sharing = false) {
    const screenShareProfile = this.profileGenerator.setMaxProfile(
      width, height, framerate, sharing,
      this.participantRemediations, this.shareRemediations,
      this.userId, this.roomId,
      (participant, profile, s) => this.setLocalProfile(participant, profile, s),
    );
    if (screenShareProfile) {
      this.screenShareProfile = screenShareProfile;
    }
  }

  private checkProblems(problem: CallProblem, sharing: boolean) {
    for (const participant of this.participants.participantsArray) {
      if (participant.id) {
        this.checkProblem(new CallProblem(participant.id, problem.message, problem.severity), sharing);
      }
    }
  }

  private setLocalProfile(participant: string, profile: RemediationProfile, sharing = false) {
    this.logging.log('IssueRemediationService: new profile', LoggingChannels.Remediation, participant, profile.id, sharing);
    if (sharing) {
      this.shareRemediations.local[participant] = profile;
    } else {
      this.participantRemediations.local[participant] = profile;
    }
    this.dataChannel.send(participant, 'remediation-profile', {
      profile: profile.id,
      sharing
    });
  }

  private logRemediation(participant: string, remediation: string, profile: RemediationProfile, sharing: boolean) {
    this.logging.logCustomEvent('remediation', {
      userId: this.userId,
      roomId: this.roomId,
      participant,
      remediation,
      profile: profile?.id || null,
      sharing,
    });
  }

  private downgrade(participant: string, sharing: boolean) {
    this.upgradeDowngrade.downgrade(
      participant, sharing,
      this.participantRemediations, this.shareRemediations,
      this.savedProfiles, this.downgradeTime,
      (p, profile, s) => this.setLocalProfile(p, profile, s),
      (p, remediation, profile, s) => this.logRemediation(p, remediation, profile, s),
      (p, s) => this.applyRemediations(p, s),
      (n) => this.display(n),
    );
  }

  private upgrade(participant: string, sharing = false) {
    this.upgradeDowngrade.upgrade(
      participant, sharing,
      this.participantRemediations, this.shareRemediations,
      this.savedProfiles, this.upgradeTime,
      (p, profile, s) => this.setLocalProfile(p, profile, s),
      (p, remediation, profile, s) => this.logRemediation(p, remediation, profile, s),
      (p, s) => this.applyRemediations(p, s),
    );
  }

  private applyRemediations(id: string, sharing: boolean) {
    this.profileApplier.applyRemediations(
      id, sharing,
      this.participantRemediations, this.shareRemediations,
      this.shareVideoTrack, this.shareAudioTrack,
    );
  }

  private display(remediationNotifications: RemediationNotification) {
    if (!remediationNotifications.notification) {
      return;
    }

    this.notification.display(remediationNotifications.message, remediationNotifications.color, 'top', 5000, [{
      side: 'end',
      icon: 'close',
      role: 'cancel',
    }]);
  }

  private findNewProfile(id: string, sharing: boolean) {
    return this.upgradeDowngrade.findNewProfile(id, sharing, this.participantRemediations, this.shareRemediations);
  }
}
